import type * as THREE from 'three'
import { UnsupportedGraphicsCapabilityError } from '@/graphics/GraphicsErrors'
import type {
  GSplatGraphicsAdapter,
  GSplatRelightingRenderRegion,
  GSplatRelightingTargetLease,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatRelightingCapability } from '@/shared/types/gsplatCapability'
import type { ViewId, ViewRect } from '@/shared/types/view'
import type { GSplatRelighting, GSplatRelightingInitializeInput } from './GSplatRelighting'

export interface GSplatRelightingLayerLease {
  readonly name: string
  readonly mask: number
  readonly owned: boolean
  release(): void
}

interface LayerEntry {
  readonly name: string
  readonly mask: number
  readonly owners: Set<object>
  readonly owned: boolean
}

/**
 * Relighting 的 named layer registry。
 *
 * Three 的 layer 是 bit mask 而不是带名字的对象，因此这里单独维护 name→mask 和 owner
 * refcount。借用已有名字的 controller 不会在最后释放时删除其它 owner 的 layer。
 */
export class GSplatRelightingLayerRegistry {
  private readonly entries = new Map<string, LayerEntry>()
  private readonly usedMasks = new Set<number>()

  acquire(name: string, owner: object): GSplatRelightingLayerLease {
    const normalizedName = name.trim()
    if (!normalizedName) throw new Error('GSplatRelighting layerName 不能为空')

    const existing = this.entries.get(normalizedName)
    if (existing) {
      existing.owners.add(owner)
      return this.createLease(existing, owner, false)
    }

    const mask = this.allocateMask()
    const entry: LayerEntry = {
      name: normalizedName,
      mask,
      owners: new Set([owner]),
      owned: true,
    }
    this.entries.set(normalizedName, entry)
    this.usedMasks.add(mask)
    return this.createLease(entry, owner, true)
  }

  getRefCount(name: string): number {
    return this.entries.get(name.trim())?.owners.size ?? 0
  }

  getMask(name: string): number | undefined {
    return this.entries.get(name.trim())?.mask
  }

  clear(): void {
    this.entries.clear()
    this.usedMasks.clear()
  }

  private createLease(
    entry: LayerEntry,
    owner: object,
    owned: boolean
  ): GSplatRelightingLayerLease {
    let released = false
    return {
      name: entry.name,
      mask: entry.mask,
      owned,
      release: () => {
        if (released) return
        released = true
        entry.owners.delete(owner)
        if (entry.owners.size > 0) return
        if (this.entries.get(entry.name) !== entry) return
        this.entries.delete(entry.name)
        this.usedMasks.delete(entry.mask)
      },
    }
  }

  private allocateMask(): number {
    // Layer 0 is Three's default layer. Keep it available to the regular GSplat scene.
    for (let channel = 1; channel < 32; channel += 1) {
      const mask = 1 << channel
      if (!this.usedMasks.has(mask)) return mask
    }
    throw new Error('GSplatRelighting named layer 数量超过 Three.js 31 个可用私有 layer')
  }
}

export interface GSplatRelightingViewControllerOptions {
  readonly adapter: GSplatGraphicsAdapter
  readonly effect: GSplatRelighting
  readonly viewId: ViewId
  readonly proxyScene: THREE.Scene
  readonly layerRegistry?: GSplatRelightingLayerRegistry
  readonly capability?: GSplatRelightingCapability
  readonly onInvalidate?: (viewId: ViewId) => void
}

export interface GSplatRelightingViewFrame {
  readonly camera: THREE.Camera
  readonly surfaceWidth: number
  readonly surfaceHeight: number
  readonly viewRect: ViewRect
}

/**
 * 单个普通 View 的 Relighting 生命周期。
 *
 * 该 controller 只编排 adapter-private target 和 proxy pass，不向宿主暴露 RenderTarget、
 * device、command encoder 或 readback；真正接入 System forward 的时机由上层 render submission
 * 决定。
 */
export class GSplatRelightingViewController {
  readonly viewId: ViewId

  private readonly adapter: GSplatGraphicsAdapter
  private readonly effect: GSplatRelighting
  private readonly proxyScene: THREE.Scene
  private readonly layerRegistry: GSplatRelightingLayerRegistry
  private readonly owner = {}
  private readonly capability?: GSplatRelightingCapability
  private readonly onInvalidate?: (viewId: ViewId) => void
  private readonly releaseEffectSubscription: () => void
  private layerLease: GSplatRelightingLayerLease | null = null
  private target: GSplatRelightingTargetLease | null = null
  private proxyCamera: THREE.Camera | null = null
  private renderRegion: GSplatRelightingRenderRegion | null = null
  private surfaceWidth = 0
  private surfaceHeight = 0
  private disposed = false
  private _enabled = false
  private suppressEffectInvalidation = false

  constructor(options: GSplatRelightingViewControllerOptions) {
    this.viewId = options.viewId
    this.adapter = options.adapter
    this.effect = options.effect
    this.proxyScene = options.proxyScene
    this.layerRegistry = options.layerRegistry ?? new GSplatRelightingLayerRegistry()
    this.capability = options.capability
    this.onInvalidate = options.onInvalidate
    this.releaseEffectSubscription = this.effect.subscribe(() => {
      if (!this.suppressEffectInvalidation) this.invalidate()
    })
  }

  get enabled(): boolean {
    return this._enabled
  }

  get layer(): GSplatRelightingLayerLease | null {
    return this.layerLease
  }

  /** 只借用 texture leaf，不返回 target 或 adapter-owned mutation API。 */
  get texture(): GSplatRelightingTargetLease['texture'] | null {
    return this.target?.texture ?? null
  }

  get targetSize(): Readonly<{ width: number; height: number }> | null {
    if (!this.target) return null
    return Object.freeze({ width: this.target.width, height: this.target.height })
  }

  get camera(): THREE.Camera | null {
    return this.proxyCamera
  }

  enable(frame: GSplatRelightingViewFrame): void {
    this.initialize(frame)
  }

  initialize(frame: GSplatRelightingViewFrame): void {
    this.assertActive()
    this.preflight(frame)
    if (this._enabled) {
      this.updateFrame(frame)
      return
    }

    let stagedLayer: GSplatRelightingLayerLease | null = null
    let stagedTarget: GSplatRelightingTargetLease | null = null
    this.suppressEffectInvalidation = true
    try {
      stagedLayer = this.layerRegistry.acquire(this.effect.layerName, this.owner)
      stagedTarget = this.createTarget(frame.surfaceWidth, frame.surfaceHeight)
      const stagedCamera = snapshotCamera(frame.camera)
      const stagedRegion = this.resolveRegion(frame, stagedTarget)
      this.effect.initialize({
        layer: stagedLayer,
        texture: this.adapter.getTextureObject(stagedTarget.texture),
      } satisfies GSplatRelightingInitializeInput)
      this.layerLease = stagedLayer
      this.target = stagedTarget
      this.proxyCamera = stagedCamera
      this.renderRegion = stagedRegion
      this.surfaceWidth = frame.surfaceWidth
      this.surfaceHeight = frame.surfaceHeight
      this.effect.setScreenSize(stagedTarget.width, stagedTarget.height)
      this._enabled = true
      stagedLayer = null
      stagedTarget = null
    } catch (error) {
      stagedTarget?.dispose()
      stagedLayer?.release()
      throw error
    } finally {
      this.suppressEffectInvalidation = false
    }
    this.invalidate()
  }

  updateFrame(frame: GSplatRelightingViewFrame): boolean {
    this.assertActive()
    this.preflight(frame)
    if (!this._enabled || !this.target || !this.layerLease) {
      this.initialize(frame)
      return true
    }

    const nextWidth = resolveTargetSize(frame.surfaceWidth, this.effect.textureScale)
    const nextHeight = resolveTargetSize(frame.surfaceHeight, this.effect.textureScale)
    const sizeChanged = nextWidth !== this.target.width || nextHeight !== this.target.height
    let nextTarget: GSplatRelightingTargetLease | null = null
    let nextLayer: GSplatRelightingLayerLease | null = null
    const previousTexture = this.effect.texture
    let changed = false
    let previousTargetToDispose: GSplatRelightingTargetLease | null = null
    this.suppressEffectInvalidation = true
    try {
      if (sizeChanged) nextTarget = this.createTarget(frame.surfaceWidth, frame.surfaceHeight)
      const layerChanged = this.layerLease.name !== this.effect.layerName.trim()
      if (layerChanged) nextLayer = this.layerRegistry.acquire(this.effect.layerName, this.owner)
      const nextCamera = snapshotCamera(frame.camera)
      const nextRegion = this.resolveRegion(frame, nextTarget ?? this.target)
      const nextTexture = nextTarget ? this.adapter.getTextureObject(nextTarget.texture) : null
      const previousTarget = this.target
      const previousLayer = this.layerLease
      const previousCamera = this.proxyCamera
      const previousRegion = this.renderRegion
      changed =
        sizeChanged ||
        layerChanged ||
        !sameCamera(previousCamera, nextCamera) ||
        !sameRegion(previousRegion, nextRegion)
      if (nextTarget) {
        this.effect.setTexture(nextTexture)
        this.effect.setScreenSize(nextTarget.width, nextTarget.height)
      }
      if (nextLayer) {
        this.effect.setLayer(nextLayer)
        this.layerLease = nextLayer
        previousLayer.release()
        nextLayer = null
      }
      this.target = nextTarget ?? this.target
      this.proxyCamera = nextCamera
      this.renderRegion = nextRegion
      this.surfaceWidth = frame.surfaceWidth
      this.surfaceHeight = frame.surfaceHeight
      previousTargetToDispose = this.target !== previousTarget ? previousTarget : null
      nextTarget = null
    } catch (error) {
      nextTarget?.dispose()
      nextLayer?.release()
      this.effect.setTexture(previousTexture)
      throw error
    } finally {
      this.suppressEffectInvalidation = false
    }
    previousTargetToDispose?.dispose()
    if (changed) this.invalidate()
    return changed
  }

  render(): boolean {
    this.assertActive()
    if (!this._enabled || !this.target || !this.proxyCamera || !this.renderRegion) return false
    this.adapter.renderPrivateRelightingScene(
      this.target,
      this.proxyScene,
      this.proxyCamera,
      this.renderRegion
    )
    return true
  }

  disable(): void {
    if (this.disposed || !this._enabled) return
    this._enabled = false
    this.suppressEffectInvalidation = true
    try {
      this.effect.disable()
      this.effect.setLayer(null)
      this.effect.setTexture(null)
    } finally {
      this.suppressEffectInvalidation = false
    }
    this.target?.dispose()
    this.target = null
    this.proxyCamera = null
    this.renderRegion = null
    this.layerLease?.release()
    this.layerLease = null
    this.invalidate()
  }

  destroy(): void {
    if (this.disposed) return
    this.disable()
    this.releaseEffectSubscription()
    this.disposed = true
  }

  private createTarget(surfaceWidth: number, surfaceHeight: number): GSplatRelightingTargetLease {
    const width = resolveTargetSize(surfaceWidth, this.effect.textureScale)
    const height = resolveTargetSize(surfaceHeight, this.effect.textureScale)
    const format = selectTargetFormat(this.adapter, this.capability)
    return this.adapter.createPrivateRelightingTarget({
      width,
      height,
      format,
      withDepth: true,
    })
  }

  private resolveRegion(
    frame: GSplatRelightingViewFrame,
    target: GSplatRelightingTargetLease
  ): GSplatRelightingRenderRegion {
    const scaleX = target.width / frame.surfaceWidth
    const scaleY = target.height / frame.surfaceHeight
    const x = Math.floor(frame.viewRect.x * scaleX)
    const width = Math.max(1, Math.min(target.width - x, Math.ceil(frame.viewRect.width * scaleX)))
    const top = Math.floor(frame.viewRect.y * scaleY)
    const height = Math.max(
      1,
      Math.min(target.height - top, Math.ceil(frame.viewRect.height * scaleY))
    )
    const y = this.adapter.backend === 'webgl2' ? target.height - top - height : top
    return { x, y, width, height }
  }

  private preflight(frame: GSplatRelightingViewFrame): void {
    this.effect.validateParameters()
    if (this.capability && !this.capability.privateTarget) {
      throw new UnsupportedGraphicsCapabilityError(
        'gsplat-relighting',
        this.adapter.backend,
        `viewId=${this.viewId}`
      )
    }
    if ((frame.camera as THREE.ArrayCamera).isArrayCamera) {
      throw new UnsupportedGraphicsCapabilityError(
        'gsplat-array-camera',
        this.adapter.backend,
        `relighting viewId=${this.viewId}`
      )
    }
    assertPositiveFinite('surfaceWidth', frame.surfaceWidth)
    assertPositiveFinite('surfaceHeight', frame.surfaceHeight)
    assertViewRect(frame.viewRect, frame.surfaceWidth, frame.surfaceHeight)
  }

  private invalidate(): void {
    this.onInvalidate?.(this.viewId)
  }

  private assertActive(): void {
    if (this.disposed) throw new Error(`GSplatRelighting View controller 已销毁: ${this.viewId}`)
  }
}

function selectTargetFormat(
  adapter: GSplatGraphicsAdapter,
  capability?: GSplatRelightingCapability
): 'rgba16float' | 'rgba8unorm' {
  const floatInfo = adapter.getTextureFormatInfo('rgba16float')
  if (
    floatInfo.renderable &&
    floatInfo.filterable &&
    (!capability ||
      (capability.floatFilterable && capability.targetFormats.includes('rgba16float')))
  ) {
    return 'rgba16float'
  }
  const unormInfo = adapter.getTextureFormatInfo('rgba8unorm')
  if (
    !unormInfo.renderable ||
    !unormInfo.filterable ||
    (capability && (!capability.unormFallback || !capability.targetFormats.includes('rgba8unorm')))
  ) {
    throw new UnsupportedGraphicsCapabilityError(
      'gsplat-relighting-target',
      adapter.backend,
      '缺少可渲染且可线性过滤的 RGBA16F/SRGBA8 target'
    )
  }
  return 'rgba8unorm'
}

function resolveTargetSize(surfaceSize: number, textureScale: number): number {
  return Math.max(1, Math.floor(surfaceSize * textureScale))
}

function snapshotCamera(camera: THREE.Camera): THREE.Camera {
  const clone = camera.clone()
  clone.matrixAutoUpdate = false
  clone.matrixWorld.copy(camera.matrixWorld)
  clone.matrixWorldInverse.copy(camera.matrixWorldInverse)
  clone.projectionMatrix.copy(camera.projectionMatrix)
  clone.projectionMatrixInverse.copy(camera.projectionMatrixInverse)
  clone.layers.mask = camera.layers.mask
  clone.updateMatrixWorld(false)
  return clone
}

function sameCamera(left: THREE.Camera | null, right: THREE.Camera): boolean {
  if (!left) return false
  return (
    left.matrixWorld.equals(right.matrixWorld) &&
    left.projectionMatrix.equals(right.projectionMatrix)
  )
}

function sameRegion(
  left: GSplatRelightingRenderRegion | null,
  right: GSplatRelightingRenderRegion
): boolean {
  return (
    left?.x === right.x &&
    left?.y === right.y &&
    left?.width === right.width &&
    left?.height === right.height
  )
}

function assertPositiveFinite(name: string, value: number): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`GSplatRelighting ${name} 非法: ${value}`)
  }
}

function assertViewRect(rect: ViewRect, surfaceWidth: number, surfaceHeight: number): void {
  if (
    !Number.isFinite(rect.x) ||
    !Number.isFinite(rect.y) ||
    !Number.isFinite(rect.width) ||
    !Number.isFinite(rect.height) ||
    rect.x < 0 ||
    rect.y < 0 ||
    rect.width <= 0 ||
    rect.height <= 0 ||
    rect.x + rect.width > surfaceWidth ||
    rect.y + rect.height > surfaceHeight
  ) {
    throw new Error(
      `GSplatRelighting viewRect 非法: ${rect.x},${rect.y},${rect.width}x${rect.height}`
    )
  }
}
